import React, { useCallback, useEffect, useId, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useWatch } from 'react-hook-form'
import { QueryResult, resultIsEngWord, resultIsThaiWord } from 'models/query-result.model'
import QueryResults from 'components/query-result/query-results'

import MainLayout from 'components/layouts/main-layout'
import { TranslationState } from 'lib/interfaces'
import QueryTruncatedAlert from 'components/homepage/translation/query-truncated-alert'
import Header from '../components/layouts/header/header'
import useTranslationForm from 'hooks/useTranslationForm'
import useSuggest from 'hooks/useSuggest'
import HomepageSuggestList from 'components/search/homepage-suggest-list'
import processQuery from 'lib/processQuery'
import { DictionaryRoute, englishRoute, thaiRoute } from 'lib/dictionary-routes'
import {
   SuggestMethod,
   trackSearch,
   trackSearchFailed,
   trackSearchResult,
   trackSuggestPick,
} from 'lib/analytics/events'

/** The dictionary entry URL a `?q=` result belongs on, if it is one entry. */
const canonicalRouteFor = (result: QueryResult): DictionaryRoute | null => {
   if (resultIsThaiWord(result)) return thaiRoute(result.dictionaryWord.word)
   if (resultIsEngWord(result)) return englishRoute(result.engDictionaryWord.word)
   return null
}

type InitialProps = {
   type: 'initial'
}
type SuccessProps = {
   type: 'success'
   preprocessedQuery: string
   processed: QueryResult
}
type ErrorProps = {
   type: 'error'
   preprocessedQuery: string
   errorStatusCode?: number
}

type LoadingState = InitialProps | SuccessProps | ErrorProps

type PopularChip =
   | {
        thai: string
        translit: string
        linkProps: ReturnType<typeof thaiRoute>
     }
   | {
        thai: string
        translit: string
        linkProps: { to: '/'; search: { q: string } }
     }

// Hand-written transliterations: the chips are a fixed teaser, so they carry
// their romanisation rather than round-tripping through a dictionary lookup.
const POPULAR_CHIPS: PopularChip[] = [
   {
      thai: 'สวัสดี',
      translit: 'sà-wàt-dee',
      linkProps: thaiRoute('สวัสดี'),
   },
   {
      thai: 'กินข้าวหรือยังครับ',
      translit: 'gin kâao rĕu yang kráp',
      linkProps: { to: '/', search: { q: 'กินข้าวหรือยังครับ' } },
   },
   {
      thai: 'ไม่เป็นไร',
      translit: 'mâi bpen rai',
      linkProps: thaiRoute('ไม่เป็นไร'),
   },
]

// The list sits inline under the box and pushes the page down, so it stays
// short: three rows is enough to recognise the word you meant, and anything
// past that is a search, not a suggestion.
const MAX_SUGGEST_ROWS = 3

const Index = () => {
   const search = useSearch({ from: '/' })
   const navigate = useNavigate()
   const [loadingState, setLoadingState] = useState<LoadingState>({
      type: 'initial',
   })
   // Seconds left on the forced cool-down after a 429; 0 means the retry button
   // is enabled (or we're not in a rate-limited state at all).
   const [retryCountdown, setRetryCountdown] = useState(0)

   const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
   const {
      onSubmit,
      resetForm,
      resetFormAndResult,
      control,
      register,
      translationState,
      queryTruncated,
      setValue,
      getValues,
      setTranslationState,
   } = useTranslationForm(textareaRef)

   const sourceText = useWatch({ control, name: 'sourceText' })
   const hasText = Boolean(sourceText && sourceText.length > 0)

   // register() supplies its own ref; merge it with our local ref so both
   // react-hook-form and the focus/reset helpers point at the same element.
   const { ref: registerRef, ...sourceTextField } = register('sourceText')

   // What the dictionary has for what's in the box right now. The box is a
   // translation box first, so this is an aside offered alongside it, never
   // something that takes the box over.
   const { items, query: suggestQuery } = useSuggest(control, 'homepage')
   const suggestRows = items.slice(0, MAX_SUGGEST_ROWS)

   // Closed until something is typed. The box is seeded from `?q=` on a reload,
   // and a list of suggestions over the translation the visitor just asked for
   // would be answering a question nobody asked.
   const [isSuggestOpen, setIsSuggestOpen] = useState(false)
   // -1 means "nothing picked", and that is the state the list appears in. This
   // box's ⏎ is a newline, so preselecting a row would take a key that already
   // had a job: type a word, press ⏎ to start the second line, and you would be
   // thrown to a dictionary entry with the first line still in the box. A row
   // becomes the ⏎ target only once ↑↓ moves onto it.
   const [suggestIndex, setSuggestIndex] = useState(-1)
   // Purely visual, and kept apart from the keyboard cursor for the same
   // reason: a mouse resting over a row must not change what ⏎ does either.
   const [suggestHoverIndex, setSuggestHoverIndex] = useState(-1)

   const suggestListboxId = useId()
   const suggestOptionId = (index: number) => `${suggestListboxId}-option-${index}`

   // Typing again drops the cursor: the rows underneath it have changed, so
   // whatever was picked no longer answers what is now in the box.
   useEffect(() => {
      setSuggestIndex(-1)
      setSuggestHoverIndex(-1)
   }, [suggestQuery, suggestRows.length])

   // Runs a query directly, bypassing the router. The normal flow is URL-driven
   // (see the effect below), but resubmitting the same text is a router no-op, so
   // the rate-limit retry needs to re-run processing imperatively.
   // Returns a cancel function: once called, this run's results are discarded so
   // a stale slow response can't overwrite a newer query's state.
   const runQuery = useCallback(
      (q: string) => {
         let cancelled = false
         setTranslationState(TranslationState.Loading)
         setRetryCountdown(0)
         const startedAt = performance.now()

         processQuery(q)
            .then((queryResult) => {
               if (cancelled) return
               trackSearchResult(queryResult, performance.now() - startedAt)

               // A `?q=` that turned out to be a single entry belongs on that
               // entry's own URL — the canonical, indexable one. Whether it is
               // an entry or a sentence to translate is only knowable after the
               // lookup, which is why this can't be a server-side redirect.
               // `replace` keeps the query URL out of the back button.
               const canonical = canonicalRouteFor(queryResult)
               if (canonical) {
                  navigate({ ...canonical, replace: true })
                  return
               }

               setLoadingState({
                  type: 'success',
                  preprocessedQuery: q,
                  processed: queryResult,
               })
               setTranslationState(TranslationState.None)
            })
            .catch((err) => {
               if (cancelled) return
               const errorStatusCode = typeof err?.status === 'number' ? err.status : undefined
               trackSearchFailed(errorStatusCode)
               setLoadingState({
                  type: 'error',
                  preprocessedQuery: q,
                  errorStatusCode,
               })
               setTranslationState(TranslationState.None)
               // The server is under heavy load - gate the retry behind a cool-down.
               if (errorStatusCode === 429) {
                  setRetryCountdown(30)
               }
            })

         return () => {
            cancelled = true
         }
      },
      [navigate, setTranslationState]
   )

   // Depend on the query *string*, not the `search` object. The object's
   // identity can change as the router settles a navigation, and re-running
   // this effect restarts the whole query: a second dictionary search, a second
   // trackSearch, and a second translation. The first run's cancel function
   // only discards its state update — it cannot recall the requests already in
   // flight. Nothing here reads `search` beyond `.q`, so this is behaviour-
   // preserving and removes the only dep that can change without the query
   // changing.
   const query = search.q
   useEffect(() => {
      // Whatever was asked for has been asked; the search that got us here is
      // over, and its suggestions go with it.
      setIsSuggestOpen(false)

      if (!query) {
         setLoadingState({ type: 'initial' })
         setTranslationState(TranslationState.None)
         return
      }

      trackSearch(query)
      return runQuery(query)
   }, [query, runQuery, setTranslationState])

   // Tick the rate-limit cool-down down to zero, one second at a time.
   useEffect(() => {
      if (retryCountdown <= 0) return
      const timer = setTimeout(() => setRetryCountdown((c) => c - 1), 1000)
      return () => clearTimeout(timer)
   }, [retryCountdown])

   const isLoading = translationState === TranslationState.Loading
   const isRateLimited = loadingState.type === 'error' && loadingState.errorStatusCode === 429
   const isCountingDown = isRateLimited && retryCountdown > 0

   const view: 'initial' | 'loading' | 'success' | 'error' = isLoading
      ? 'loading'
      : loadingState.type

   // Single-word Thai/English dictionary lookups get the compact header (logo
   // left, labelled nav, inline search bar). Multi-word translations keep the
   // big homepage box.
   const isWordPage =
      view === 'success' &&
      loadingState.type === 'success' &&
      (resultIsThaiWord(loadingState.processed) || resultIsEngWord(loadingState.processed))

   // Rows or nothing: with no empty state left to draw, a lookup that matched
   // nothing simply has no list. Never over the compact word-page box (that one
   // has its own panel), and never mid-translation, when the answer is already
   // on its way.
   const showSuggest = !isWordPage && view !== 'loading' && isSuggestOpen && suggestRows.length > 0
   const selectedSuggest = showSuggest && suggestIndex >= 0 ? suggestRows[suggestIndex] : undefined

   // Reports the pick and shuts the list. Navigation is the caller's: a click
   // is the row's own <Link> following itself, ⏎ has to be sent.
   const pickSuggest = (index: number, method: SuggestMethod) => {
      const item = suggestRows[index]
      if (!item) return
      trackSuggestPick({
         surface: 'homepage',
         method,
         index,
         rowCount: suggestRows.length,
         isThaiHeadword: item.isThaiHeadword,
         query: suggestQuery,
      })
      setIsSuggestOpen(false)
   }

   const btnLabel = isCountingDown
      ? `Retry in ${retryCountdown}s`
      : isRateLimited
        ? 'Retry'
        : 'Translate'

   return (
      <MainLayout>
         <div className="bg-white flex flex-col min-h-full flex-1">
            <Header
               onClick={resetForm}
               onModalClose={() => {
                  onSubmit()
               }}
               compact={isWordPage}
            >
               <h1
                  className={`mt-[30px] mb-[22px] max-h-[140px] overflow-hidden text-center text-[30px] sm:text-[40px] font-extrabold leading-[1.12] tracking-[-0.3px] sm:tracking-[-0.6px] text-bodyBlack ${
                     isWordPage ? 'max-h-0 m-0 opacity-0' : ''
                  }`}
               >
                  Thai translation, broken down
               </h1>
               <p
                  className={`max-w-[720px] mx-auto max-h-[200px] overflow-hidden text-center text-[16px] sm:text-[18px] leading-[1.55] text-[#7d8794] pretty ${
                     isWordPage ? 'max-h-0 m-0 opacity-0' : ''
                  }`}
               >
                  Paste any Thai you come across — a chat message, a menu, a street sign. We&apos;ll
                  translate it, then walk you through it word by word: what each one means and how
                  to say it, with audio and examples along the way.
               </p>

               <form
                  onSubmit={onSubmit}
                  className={`relative mt-[20px] bg-white border border-hairline rounded-[16px] shadow-card transition-colors duration-200 focus-within:border-lightBlue ${
                     isWordPage
                        ? 'mt-0 rounded-[12px] shadow-none bg-searchFieldBg border-hairline focus-within:border-translit'
                        : ''
                  }`}
               >
                  <div
                     className={`flex items-start gap-[8px] ${isWordPage ? 'items-center pr-[7px]' : ''}`}
                  >
                     <textarea
                        ref={(el) => {
                           registerRef(el)
                           textareaRef.current = el
                        }}
                        className={`block flex-1 min-w-0 w-full border-0 outline-none resize-none overflow-hidden bg-transparent rounded-t-[16px] p-[28px_54px_24px_22px] font-latin text-[19px] leading-[1.6] text-bodyBlack min-h-[136px] placeholder:font-inherit placeholder:not-italic placeholder:text-[#b3bac4] ${
                           isWordPage
                              ? 'min-h-[38px] p-[8px_0_8px_20px] rounded-[12px] leading-[1.4]'
                              : ''
                        }`}
                        placeholder={
                           isWordPage
                              ? 'Look up a word in Thai or English…'
                              : "Thai, English, or transliterated Thai — we'll figure it out"
                        }
                        aria-label="searchbox"
                        data-gramm="false"
                        rows={1}
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={showSuggest}
                        aria-controls={showSuggest ? suggestListboxId : undefined}
                        aria-activedescendant={
                           selectedSuggest ? suggestOptionId(suggestIndex) : undefined
                        }
                        {...sourceTextField}
                        onChange={(e) => {
                           setIsSuggestOpen(true)
                           void sourceTextField.onChange(e)
                        }}
                        onKeyDown={(e) => {
                           if (e.nativeEvent.isComposing) return

                           if (e.key === 'Escape') {
                              setIsSuggestOpen(false)
                              return
                           }

                           // Shift + Enter translates, whatever the list is
                           // showing — the box's own job always wins.
                           if (e.key === 'Enter' && (e.shiftKey || e.metaKey || e.ctrlKey)) {
                              e.preventDefault()
                              setIsSuggestOpen(false)
                              onSubmit()
                              return
                           }

                           if (!showSuggest) return

                           if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                              e.preventDefault()
                              setSuggestHoverIndex(-1)
                              // The walk includes -1, the typed text itself, as
                              // a stop between the last row and the first:
                              // arrowing off either end hands the box back
                              // rather than looping straight past it.
                              const stops = suggestRows.length + 1
                              const from = suggestIndex + 1
                              const next =
                                 e.key === 'ArrowDown'
                                    ? (from + 1) % stops
                                    : (from - 1 + stops) % stops
                              setSuggestIndex(next - 1)
                              return
                           }

                           // Only a row ↑↓ actually moved onto wins. With
                           // nothing picked ⏎ stays the newline it has always
                           // been, so a multi-line paste can still be typed out
                           // in the box.
                           if (e.key === 'Enter' && selectedSuggest) {
                              e.preventDefault()
                              pickSuggest(suggestIndex, 'enter')
                              navigate(selectedSuggest.route)
                           }
                        }}
                     />
                     {hasText && (
                        <button
                           type="button"
                           className={`shrink-0 w-0 opacity-0 overflow-hidden pointer-events-none inline-flex items-center justify-center cursor-pointer text-[#b3bac4] transition-colors duration-150 hover:text-bodyBlack [&_.material-icons]:text-[20px] ${
                              isWordPage ? 'w-[20px] opacity-100 pointer-events-auto' : ''
                           }`}
                           title="Clear"
                           aria-label="Clear"
                           onClick={resetFormAndResult}
                        >
                           <span className="material-icons">close</span>
                        </button>
                     )}
                     <button
                        type="submit"
                        className={`shrink-0 h-[40px] w-0 opacity-0 overflow-hidden pointer-events-none inline-flex items-center justify-center border-none rounded-full bg-transparent cursor-pointer text-translit transition-colors duration-150 hover:bg-lightBlueTransparent [&_.material-icons]:text-[22px] ${
                           isWordPage ? 'w-[40px] opacity-100 pointer-events-auto' : ''
                        }`}
                        title="Translate"
                        aria-label="Translate"
                     >
                        <span className="material-icons">arrow_forward</span>
                     </button>
                  </div>
                  <div
                     className={`flex flex-wrap items-center justify-end sm:justify-between gap-[12px] max-h-[80px] overflow-hidden p-[12px_14px_14px_20px] border-t border-t-[#eef1f5] ${
                        isWordPage ? 'max-h-0 opacity-0 py-0 border-transparent' : ''
                     }`}
                  >
                     <span className="hidden sm:inline-block text-[11px] font-bold tracking-[0.8px] uppercase text-[#98a2b3]">
                        Shift + enter to translate
                     </span>
                     <button
                        type={isRateLimited ? 'button' : 'submit'}
                        className="inline-flex items-center justify-center gap-[8px] px-[30px] py-[11px] text-[16px] font-bold font-inherit text-white bg-translit border-none rounded-[8px] cursor-pointer transition-colors duration-150 hover:enabled:bg-[#2559d6] disabled:bg-[#aab3c0] disabled:cursor-default"
                        disabled={isLoading || isCountingDown}
                        aria-label={isRateLimited ? 'Retry' : 'Translate'}
                        onClick={(e) => {
                           // In the rate-limited state the button re-runs the same
                           // query directly (the form submit would be a no-op).
                           if (isRateLimited) {
                              e.preventDefault()
                              if (retryCountdown === 0) {
                                 runQuery(loadingState.preprocessedQuery)
                              }
                           }
                        }}
                     >
                        {btnLabel}
                     </button>
                  </div>
                  {hasText && !isWordPage && (
                     <button
                        type="button"
                        className="absolute top-[13px] right-[13px] w-[32px] h-[32px] grid place-items-center rounded-full border-none bg-transparent cursor-pointer text-[#b3bac4] transition-all duration-150 hover:bg-gray1 hover:text-bodyBlack [&_.material-icons]:text-[20px]"
                        title="Clear"
                        aria-label="Clear"
                        onClick={resetFormAndResult}
                     >
                        <span className="material-icons">close</span>
                     </button>
                  )}
               </form>

               {showSuggest && (
                  <HomepageSuggestList
                     items={suggestRows}
                     selectedIndex={suggestIndex}
                     hoveredIndex={suggestHoverIndex}
                     onHover={setSuggestHoverIndex}
                     onPick={(index) => pickSuggest(index, 'click')}
                     listboxId={suggestListboxId}
                     optionId={suggestOptionId}
                  />
               )}

               {/* Announced rather than shown: the rows are right there on the
                   page, but a screen reader needs to hear the list change. */}
               <span className="sr-only" role="status" aria-live="polite">
                  {showSuggest
                     ? `${suggestRows.length} suggestion${suggestRows.length === 1 ? '' : 's'}`
                     : ''}
               </span>
            </Header>

            {!isWordPage && view !== 'success' && (
               <main className="max-w-[900px] w-full mx-auto px-[8px] sm:px-[20px] pb-[56px]">
                  {isRateLimited && (
                     <div className="mt-[22px] rounded-[8px] p-[14px_20px_16px] bg-[#fdf6e7]">
                        <div className="text-[11px] font-bold tracking-[0.7px] uppercase text-[#b9873a]">
                           Easy does it
                        </div>
                        <div className="mt-[6px] text-[16px] text-bodyBlack pretty">
                           <p>
                              Our server is under heavy load. Please wait a short time and try
                              again.
                           </p>
                           <p className="mt-5">Sorry - we know it's frustrating.</p>
                           <p className="mt-5">
                              Our translation is powered by AI, which has to be paid for. In order
                              to continue to provide a free service, we sometimes have to limit
                              requests at busy times.
                           </p>
                        </div>
                        <div></div>
                     </div>
                  )}

                  {/* An empty box only. Once something is typed the space under
                      the box belongs to what the dictionary has for it. */}
                  {view === 'initial' && !hasText && (
                     <div className="flex items-center justify-center gap-[10px] flex-wrap mt-[24px]">
                        <span className="text-[11px] font-bold tracking-[0.8px] uppercase text-[#98a2b3] inline-flex items-center gap-[6px] mr-[6px] [&_.material-icons]:text-[17px] [&_.material-icons]:text-[#98a2b3]">
                           <span className="material-icons">lightbulb_outline</span>Try it out
                        </span>
                        {POPULAR_CHIPS.map((chip) => (
                           <Link
                              key={chip.thai}
                              {...chip.linkProps}
                              className="inline-flex items-baseline gap-[9px] bg-backgroundGray border border-hairline px-[15px] py-[7px] rounded-full cursor-pointer no-underline transition-all duration-150 hover:bg-gray1 hover:border-lightBlue"
                           >
                              <span className="font-thai text-[17px] text-thaiWord">
                                 {chip.thai}
                              </span>
                              <span className="font-translit text-[13px] italic text-translit">
                                 {chip.translit}
                              </span>
                           </Link>
                        ))}
                     </div>
                  )}

                  {view === 'loading' && (
                     <div className="flex flex-col items-center gap-[14px] py-[64px]">
                        <div className="w-[44px] h-[44px] border-[4px] border-lightBlueTransparent border-t-translit rounded-full animate-spin" />
                        <span className="text-[11px] font-bold tracking-[0.8px] uppercase text-[#98a2b3]">
                           Working on it
                        </span>
                     </div>
                  )}

                  {view === 'error' && !isRateLimited && (
                     <div className="mt-[22px] rounded-[8px] p-[14px_20px_16px] bg-[#fdf1ef]">
                        <div className="text-[11px] font-bold tracking-[0.7px] uppercase text-[#d6472f]">
                           That one got away from us
                        </div>
                        <div className="mt-[6px] text-[16px] text-bodyBlack pretty">
                           Something went wrong on our end — your text is still here, so press
                           translate to give it another go.
                        </div>
                     </div>
                  )}
               </main>
            )}

            {queryTruncated && <QueryTruncatedAlert />}

            {view === 'success' && loadingState.type === 'success' && (
               <QueryResults
                  queryResult={loadingState.processed}
                  preprocessedQuery={loadingState.preprocessedQuery}
                  setValue={setValue}
                  getValues={getValues}
                  setTranslationState={setTranslationState}
               />
            )}
         </div>
      </MainLayout>
   )
}

export default Index
